import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { CampusUserGroup } from '@chancenwerk/db/CampusUserGroup.js';
import { Adresse } from '@chancenwerk/db/adresse.js';
import { CampusNotification } from '@chancenwerk/db/notifications/CampusNotification.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/design/components/cr-flex.js';
import { School } from '@chancenwerk/master-data-management/partner/entities/School.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filter, of, switchMap } from 'rxjs';

const groupSelector: BaseQLSelectionSet<CampusUserGroup> = {
  _id: 1,
  _class: 1,
  name: 1,
};

const partnerSelector: BaseQLSelectionSet<School> = {
  _id: 1,
  _class: 1,
  name: 1,
  kuerzel: 1,
  address: Adresse.allFields,
};

/**
 * @customElement campus-dialog-send-notification
 */
@customElement('campus-dialog-send-notification')
export class CampusDialogSendNotification extends ChromiumDialog<boolean> {
  @property({ attribute: false }) public notification = new RXController(this, new CampusNotification({}));

  @state() private readonly _groups = new RXController(
    this,
    CampusUserGroup.subscribeAll<CampusUserGroup>()(groupSelector),
  );

  @state() private readonly _partners = new RXController(this, School.subscribeAll<School>()(partnerSelector));

  @state() private readonly _hasPermissionForGlobalScope = new RXController(
    this,
    currentUser<ChancenwerkUser>().pipe(
      filter(Boolean),
      switchMap(user =>
        !user ? of(false) : of(user.hasPermission(ChancenPermission.global.EDIT_NOTIFICATIONS, GLOBAL_CONTEXT)),
      ),
    ),
  );

  static override get element_name() {
    return 'campus-dialog-send-notification';
  }

  private readonly _form = new FormController(this, this.notification.observable, {
    disabledFields: this._hasPermissionForGlobalScope.value ? null : { forEmployees: 1 },
    success: async notification => {
      if (!this._hasPermissionForGlobalScope.value && !notification.forSchoolIDs?.length) {
        notification.forSchoolIDs = this._partners.value?.map(p => p._id);
      }

      if (notification._id) {
        await notification.save();
      } else {
        await notification.create();
      }
      await this.close(true);
    },
  });

  override heading = 'Neuigkeit versenden';

  override content() {
    if (this._partners.isLoading || this._groups.isLoading)
      return html`
        <cr-flex space="md" padding="md" center crossaxis-center>
          <cr-campus-loader></cr-campus-loader>
        </cr-flex>
      `;
    return html`
      <cr-flex space="md" padding="md">
        <cr-text bold>Bei keinem ausgewählten Filter, geht die Neuigkeit an alle deine Schulen!</cr-text>
        <cr-dropdown-selection
          clearable
          ${this._form.field('forEmployees')}
          placeholder="Zielgruppe"
          .selectables=${[true, false]}
          .renderString=${(item: boolean | null) => (item ? 'Mitarbeitende' : 'Schüler:innen')}
          @value-picked=${(e: ValueEvent<boolean>) => {
            this.requestUpdate();
          }}
        ></cr-dropdown-selection>
        ${this.notification.value.forEmployees === false
          ? html`
              <cr-dropdown-multiselection
                ${this._form.field('forGrades')}
                clearable
                placeholder="Für Jahrgangsstufen"
                .selectables=${ChancenwerkUser.availableGrades}
              ></cr-dropdown-multiselection>
              <cr-dropdown-multiselection
                clearable
                placeholder="Gruppe"
                ${this._form.field('forGroupIDs')}
                .renderString=${(item: string) => this._groups.value?.find(g => g._id === item)?.name}
                .search=${(search?: string) =>
                  this._groups.value
                    ?.filter(g => g.name.toLocaleLowerCase().includes(search?.toLocaleLowerCase() ?? ''))
                    .map(g => g._id)}
              ></cr-dropdown-multiselection>
              <cr-dropdown-multiselection
                clearable
                placeholder="Schule"
                ${this._form.field('forSchoolIDs')}
                .renderString=${(item: string) => {
                  const partner = this._partners.value?.find(p => p._id === item);
                  if (!partner) return '';
                  return (partner.name ? partner.name : partner.kuerzel) + ' ' + (partner.address?.zipCity?.city ?? '');
                }}
                .search=${(search?: string) =>
                  this._partners.value
                    ?.filter(g => g.name?.toLocaleLowerCase().includes(search?.toLocaleLowerCase() ?? ''))
                    .map(p => p._id)}
              ></cr-dropdown-multiselection>
            `
          : nothing}
        <cr-button
          @click=${async () => {
            if (!this._hasPermissionForGlobalScope.value && !this._partners.value?.length) {
              const errorString = 'Du hast keine zugewiesenen Schulen an die du Neuigkeiten versenden könntest';
              void ChromiumDialog.alert(errorString);
              throw new Error(errorString);
            }
            if (
              await ChromiumDialog.confirm('Die Neuigkeit wirklich versenden?', {
                acceptText: 'Jetzt versenden!',
                declineText: 'Abbrechen',
              })
            ) {
              await ChromiumDialog.guard(() => this._form.validate());
            }
          }}
        >
          Jetzt senden!
        </cr-button>
      </cr-flex>
    `;
  }

  public override getInnerWrapper(options: DialogOptions) {
    const wrapperElem = super.getInnerWrapper(options as Required<DialogOptions>);

    if (!options.draggable) {
      wrapperElem.style.setProperty('min-width', '40vw');
    }
    wrapperElem.style.setProperty('background-color', 'white');
    return wrapperElem;
  }

  override cancel(returnVal?: any): Promise<void> {
    return super.cancel(false);
  }
}
